import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { supabase } from '@/integrations/supabase/client';

export type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeContextProps {
  isDarkMode: boolean;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const storedTheme = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
    return isThemeMode(storedTheme) ? storedTheme : 'system';
  });
  const [systemIsDark, setSystemIsDark] = useState(systemPrefersDark);
  const isDarkMode = themeMode === 'system' ? systemIsDark : themeMode === 'dark';

  useEffect(() => {
    const root = window.document.documentElement;
    const applyResolvedTheme = (): void => {
      const lockedDark = root.getAttribute('data-auth-theme-lock') === 'dark';
      const useDark = lockedDark || isDarkMode;
      root.classList.toggle('dark', useDark);
      root.classList.toggle('light', !useDark);
      root.setAttribute('data-theme', useDark ? 'dark' : 'light');
    };

    applyResolvedTheme();
    localStorage.setItem('theme', themeMode);

    window.addEventListener('mora-auth-theme-lock-change', applyResolvedTheme);
    return () => window.removeEventListener('mora-auth-theme-lock-change', applyResolvedTheme);
  }, [isDarkMode, themeMode]);

  // System mode is a live preference, not a one-time default. Follow device
  // changes while selected and stop listening when the user chooses a fixed
  // theme.
  useEffect(() => {
    if (themeMode !== 'system' || typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemTheme = (event?: MediaQueryListEvent): void => {
      setSystemIsDark(event?.matches ?? query.matches);
    };
    syncSystemTheme();
    query.addEventListener('change', syncSystemTheme);
    return () => query.removeEventListener('change', syncSystemTheme);
  }, [themeMode]);

  // Settings writes `theme_mode` plus the legacy `dark_mode` boolean to auth
  // metadata. Subscribe directly here because ThemeProvider wraps AuthProvider
  // in main.tsx and therefore cannot consume useAuth.
  // `appliedRemoteRef` guards against re-applying the remote value after the
  // user has since chosen a theme locally in the same session.
  const appliedRemoteRef = useRef(false);
  useEffect(() => {
    const applyRemote = (metadata: Record<string, unknown> | undefined) => {
      if (appliedRemoteRef.current) return;
      const remoteMode = metadata?.theme_mode;
      const legacyDark = metadata?.dark_mode;
      const nextMode = isThemeMode(remoteMode)
        ? remoteMode
        : typeof legacyDark === 'boolean'
          ? legacyDark
            ? 'dark'
            : 'light'
          : null;
      if (!nextMode) return;
      appliedRemoteRef.current = true;
      setThemeModeState(nextMode);
    };

    void supabase.auth.getUser().then(({ data }) => {
      applyRemote(data.user?.user_metadata);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      applyRemote(session?.user?.user_metadata);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode): void => {
    // A deliberate local toggle wins over any later remote-sync attempt for
    // the rest of this session.
    appliedRemoteRef.current = true;
    setThemeModeState(mode);
  }, []);

  // Retained for small legacy binary toggles elsewhere in the app. Choosing
  // either side explicitly leaves System mode, as users expect from a switch.
  const toggleTheme = useCallback((): void => {
    setThemeMode(isDarkMode ? 'light' : 'dark');
  }, [isDarkMode, setThemeMode]);

  const value = useMemo<ThemeContextProps>(
    () => ({ isDarkMode, themeMode, setThemeMode, toggleTheme }),
    [isDarkMode, setThemeMode, themeMode, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
